import type { GraphQLResult } from 'aws-amplify/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  ProductCreationStatus,
  ProductCreationStatusProps,
  ProductRestoration,
  ProductStatus,
  ProductStatusInput,
  ProductStatusProps,
  ProductStatusUpdate,
  ProductUpdateStatusProps,
} from '@skop-app/lib-domain/types'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  closeAndCreateProductStatusMutation,
  createProductBatchMutation,
  createProductStatusMutation,
  createProductWithStatusMutation,
  updatedProductBatchMutation,
  updateProductStatusMutation,
} from '@/services/graphql'
import {
  buildCreateProductWithStatusInput,
  type ProductWithStatusCreation,
} from '@/services/product/createProductWithStatus'
import { usePrepareProductCreateAuthorizerScope } from '@/services/product/productCreateAuthorizerScope'

import { updateProductStatusCache } from './product'

const client = getAmplifyClient()

// Create a product and its first status
export const useCreateProduct = () => {
  const queryClient = useQueryClient()
  const prepareProductCreateAuthorizerScope =
    usePrepareProductCreateAuthorizerScope()

  return useMutation({
    mutationKey: ['createProduct'],
    mutationFn: createProduct,
    onMutate: async (variables) => {
      await prepareProductCreateAuthorizerScope({
        organizationID:
          variables.productStatusInput.organizationID ??
          variables.organizationID,
        organizationMasterID: variables.productStatusInput.organizationMasterID,
      })
      updateProductStatusCache(variables, queryClient, ['productStatuses'])
    },
  })
}

// Update a status with low importance values (that does not require to create a new status for history)
export const useUpdateProductStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateProductStatus'],
    mutationFn: updateProductStatus,
    onMutate: async (variables: ProductCreationStatusProps) => {
      updateProductStatusCache(variables, queryClient, ['productStatuses'])
    },
    meta: {
      silent: true,
    },
  })
}

/**
 * Create a new status for a product by closing the previous status and creating a new one
 * @param byUserID User ID of the user creating the status, needed for cache update (should be renamed to userID)
 * @param organizationID Organization ID of the user creating the status, needed for cache update
 * @param previousStatusInput Previous status to update
 * @param productStatusInput Object containing the new status values (full product object)
 * @returns Data with createdAt date of the new status and mutation state (loading, error, etc.)
 */
export const useCreateProductStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createProductStatus'],
    mutationFn: closeAndCreateProductStatus,
    onMutate: async (variables: ProductStatusProps) => {
      const { byUserID, organizationID, previousStatusInput } = variables

      // Remove or update old status in "Inventory" cache
      updateProductStatusCache(
        {
          organizationID,
          productStatusInput: previousStatusInput,
        },
        queryClient,
        ['productStatuses'],
      )
      // Add to "Inventory" cache new status
      updateProductStatusCache(variables, queryClient, ['productStatuses'])

      // Also remove from "Corbeille" cache if product is deleted
      if (
        variables.productStatusInput.type === 'deleted' &&
        variables.productStatusInput.progress === 'done'
      ) {
        const typeVariables: ProductRestoration = {
          byUserID,
          organizationID: organizationID,
          productStatusInput: variables.productStatusInput,
        }
        updateProductStatusCache(typeVariables, queryClient, ['productType'])
      }
    },
    onSuccess: async (data, variables) => {
      // Refresh Project cache if needed
      if (variables.productStatusInput.projectID) {
        await queryClient.invalidateQueries({
          queryKey: ['project'],
        })
        await queryClient.invalidateQueries({
          queryKey: ['projects'],
        })
      }
    },
  })
}

// Queries
export const updateProductStatus = async ({
  productStatusInput,
}: ProductUpdateStatusProps) => {
  try {
    const { data } = (await client.graphql({
      query: updateProductStatusMutation,
      variables: { input: productStatusInput },
    })) as GraphQLResult<ProductStatusUpdate>

    // if status was batched, update the relation in ProductBatch table with the new status
    // batchedProductID is the ID of the relation in ProductBatch table
    // batchedStatusID is the ID of the new status
    if (productStatusInput.batchedProductID) {
      await client.graphql({
        query: updatedProductBatchMutation,
        variables: {
          input: {
            id: productStatusInput.batchedProductID,
            batchedStatusID: productStatusInput.id,
          },
        },
      })
    }

    return data?.updateProductStatus.updatedAt
  } catch (e) {
    console.error('Error updating product status', e)
    return e
  }
}

const createProduct = async ({
  byUserID,
  organizationID,
  productInput,
  productStatusInput,
}: ProductCreationStatusProps) => {
  try {
    const input = buildCreateProductWithStatusInput({
      organizationID: productStatusInput.organizationID ?? organizationID,
      productID: productInput.id,
      productName: productInput.current.name,
      productNature: productStatusInput.nature,
      productStatusInput: {
        ...productStatusInput,
        byUserID,
      },
    })
    const { data } = (await client.graphql({
      query: createProductWithStatusMutation,
      variables: { input },
    })) as GraphQLResult<ProductWithStatusCreation>
    return {
      product: data?.createProductWithStatus.product.createdAt,
      productStatus: data?.createProductWithStatus.productStatus.createdAt,
    }
  } catch (e) {
    console.error('Error creating product', e)
    throw e
  }
}

const closeAndCreateProductStatus = async (params: ProductStatusProps) => {
  const { previousStatusInput, productStatusInput } = params
  try {
    const { data } = (await client.graphql({
      query: closeAndCreateProductStatusMutation,
      variables: { previousStatusInput, input: productStatusInput },
    })) as GraphQLResult<ProductStatus>
    return data?.createProductStatus.createdAt
  } catch (e) {
    console.error('Error creating product status', e)
    throw e
  }
}

export const createProductStatus = async ({
  productStatusInput,
}: {
  productStatusInput: ProductStatusInput
}) => {
  try {
    const { data } = (await client.graphql({
      query: createProductStatusMutation,
      variables: { input: productStatusInput },
    })) as GraphQLResult<ProductCreationStatus>
    return data?.createProductStatus.createdAt
  } catch (e) {
    console.error('Error creating product status', e)
    throw e
  }
}

export const createProductBatch = async ({ id, productID }) => {
  // Create a ProductBatch
  // ProductStatus.batchedProductID === ProductBatch.id
  // ProductBatch.batchedStatusID === ProductStatus.id
  // ProductBatch.productID === Product.id
  return await client.graphql({
    query: createProductBatchMutation,
    variables: {
      input: {
        id,
        productID,
        batchedStatusID: id,
      },
    },
  })
}
